import React, { useState, useMemo } from "react";
import { MemberSearch, NeoButton } from "../../../../../components/Common";
import { useMembers } from "../../../../../hooks/useMembers";

/**
 * Componente de seleção de aluno com busca.
 * Exibe badge do aluno selecionado ou campo de busca.
 *
 * @param {Object}   selectedMember  - { id, name, photo } ou null
 * @param {Function} onSelect        - Callback(member) ao selecionar
 * @param {Function} onClear         - Callback() ao remover seleção
 * @param {string}   label           - Label do campo
 * @param {boolean}  required        - Mostrar asterisco
 */
const MemberSelector = ({
    selectedMember = null,
    onSelect,
    onClear,
    label = "Aluno",
    required = false,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const { members } = useMembers();

    const candidates = useMemo(() => {
        if (!searchTerm || searchTerm.length < 2) return [];
        const term = searchTerm.toLowerCase();
        return members
            .filter(m => m.status === "active")
            .filter(m => {
                const name = (m.fullName || m.firstName || "").toLowerCase();
                const number = (m.memberNumber || "").toString();
                return name.includes(term) || number.includes(term);
            })
            .slice(0, 8)
            .map(m => ({
                id: m.id,
                name: m.fullName || m.firstName || "Membro",
                photo: m.photoUrl || null,
                memberNumber: m.memberNumber || null,
                cellPhone: m.cellPhone || null,
                phone: m.phone || null,
            }));
    }, [searchTerm, members]);

    return (
        <div className="mb-4 p-3 bg-white rounded-3 shadow-sm" style={{ border: "1.5px solid rgba(72, 39, 18, 0.12)" }}>
            <label className="neo-input-label mb-2 d-flex align-items-center">
                <i className="mdi mdi-account-outline me-1" />
                {label} {required && <span className="text-danger ms-1">*</span>}
            </label>
            {selectedMember?.id ? (
                <div className="d-flex align-items-center gap-3 p-2 bg-light rounded" style={{ border: "1px solid rgba(72, 39, 18, 0.08)" }}>
                    <div className="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 40, height: 40, overflow: "hidden" }}>
                        {selectedMember.photo ? (
                            <img src={selectedMember.photo} alt="" className="rounded-circle" style={{ width: 40, height: 40, objectFit: "cover" }} />
                        ) : (
                            <i className="mdi mdi-account text-primary font-size-20" />
                        )}
                    </div>
                    <div className="flex-grow-1">
                        <p className="mb-0 fw-bold text-brand-primary font-size-14" style={{ fontFamily: "Outfit, sans-serif" }}>{selectedMember.name}</p>
                        <span className="text-muted font-size-11 fw-semibold">Treino individual</span>
                    </div>
                    {onClear && (
                        <NeoButton
                            type="button"
                            variant="soft-danger"
                            size="sm"
                            square
                            icon="mdi-close"
                            onClick={onClear}
                            title="Remover aluno"
                        />
                    )}
                </div>
            ) : (
                <MemberSearch
                    value={searchTerm}
                    onChange={setSearchTerm}
                    candidates={candidates}
                    showNoResults={searchTerm.length >= 2 && candidates.length === 0}
                    placeholder="Buscar aluno pelo nome ou ID..."
                    onSelect={(member) => {
                        onSelect(member);
                        setSearchTerm("");
                    }}
                />
            )}
        </div>
    );
};

export default MemberSelector;
